import React, { useState, useMemo } from 'react';
import { View, ScrollView, TextInput, StyleSheet, TouchableOpacity } from 'react-native';
import { Text } from '@app/design-system';
import { useThemeColors, useThemeAccent } from '@app/core';
import { BLOCK_REGISTRY } from '../../constants/blockTypes';
import { BlockCard } from './BlockCard';

const CATEGORIES = [
  { id: 'all', name: 'All', icon: '🎨' },
  { id: 'header', name: 'Header', icon: '📌' },
  { id: 'hero', name: 'Hero', icon: '🎭' },
  { id: 'content', name: 'Content', icon: '📝' },
  { id: 'products', name: 'Products', icon: '🛍️' },
  { id: 'media', name: 'Media', icon: '🖼️' },
  { id: 'social', name: 'Social', icon: '📱' },
  { id: 'advanced', name: 'Advanced', icon: '⚙️' },
];

export const LeftPanel: React.FC = () => {
  const themeColors = useThemeColors() || { primary: '#34C759', secondary: '#F5F5F5', text: '#000000', background: '#FFFFFF' };
  const accentColor = useThemeAccent() || '#FF9500';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredBlocks = useMemo(() => {
    let blocks = Object.values(BLOCK_REGISTRY);

    // Filter by category
    if (selectedCategory !== 'all') {
      blocks = blocks.filter(block => block.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      blocks = blocks.filter(block =>
        block.name.toLowerCase().includes(query) ||
        block.description.toLowerCase().includes(query)
      );
    }

    return blocks;
  }, [searchQuery, selectedCategory]);

  const styles = StyleSheet.create({
    container: {
      width: 320,
      backgroundColor: themeColors.primary,
      borderRightWidth: 1,
      borderRightColor: themeColors.border || "#E5E5E5",
      flexDirection: 'column',
    },
    header: {
      padding: 16,
      borderBottomWidth: 1,
      borderBottomColor: themeColors.border || "#E5E5E5",
    },
    headerTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: themeColors.text || "#000000",
      marginBottom: 12,
    },
    searchInput: {
      height: 40,
      backgroundColor: themeColors.secondary,
      borderWidth: 1,
      borderColor: themeColors.border || "#E5E5E5",
      borderRadius: 8,
      paddingHorizontal: 12,
      fontSize: 14,
      color: themeColors.text || "#000000",
    },
    categories: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      marginTop: 12,
    },
    categoryButton: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: themeColors.border || "#E5E5E5",
      backgroundColor: themeColors.secondary,
    },
    categoryButtonActive: {
      backgroundColor: accentColor,
      borderColor: accentColor,
    },
    categoryText: {
      fontSize: 12,
      color: themeColors.text || "#000000",
    },
    categoryTextActive: {
      color: '#FFFFFF',
      fontWeight: '600',
    },
    blockList: {
      flex: 1,
      padding: 16,
    },
    emptyState: {
      padding: 32,
      alignItems: 'center',
    },
    emptyText: {
      fontSize: 14,
      color: themeColors.text || "#666666",
      textAlign: 'center',
    },
  });

  return (
    <View style={styles.container}>
      {/* Header with Search */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>📦 Blocks</Text>

        <TextInput
          style={styles.searchInput}
          placeholder="Search blocks..."
          placeholderTextColor={themeColors.text || "#666666"}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />

        {/* Categories */}
        <View style={styles.categories}>
          {CATEGORIES.map(category => (
            <TouchableOpacity
              key={category.id}
              style={[
                styles.categoryButton,
                selectedCategory === category.id && styles.categoryButtonActive,
              ]}
              onPress={() => setSelectedCategory(category.id)}
            >
              <Text
                style={[
                  styles.categoryText,
                  selectedCategory === category.id && styles.categoryTextActive,
                ]}
              >
                {category.icon} {category.name}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Block List */}
      <ScrollView style={styles.blockList}>
        {filteredBlocks.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>
              No blocks found.{'\n'}Try a different search or category.
            </Text>
          </View>
        ) : (
          filteredBlocks.map(blockMetadata => (
            <BlockCard key={blockMetadata.type} metadata={blockMetadata} />
          ))
        )}
      </ScrollView>
    </View>
  );
};

